import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import './DashboardNavbarFacilitySelection.css';
import PropTypes from 'prop-types';
import { MenuItem, Select } from '@mui/material';
import facilities from '../../../config/facilities.json';

const DashboardNavbarFacilitySelection = forwardRef(
  function DashboardNavbarFacilitySelection(
    { onChange, allowedFacilities = [], initialValue = null },
    ref,
  ) {
    const initialStoredValue =
      localStorage.getItem('selectedFacility') || initialValue || '';
    const [selectedFacility, setSelectedFacility] =
      useState(initialStoredValue);
    const [isValidSelection, setIsValidSelection] = useState(false);

    useEffect(() => {
      // Check if the stored or initial value is in the updated allowedFacilities
      const validFacilities = allowedFacilities.map((f) => f.id);
      const isValid = validFacilities.includes(initialStoredValue);
      setIsValidSelection(isValid);
      if (isValid) {
        setSelectedFacility(initialStoredValue);
      } else if (allowedFacilities.length && !isValid) {
        setSelectedFacility(''); // Reset if not valid and allowedFacilities are loaded
      }
    }, [allowedFacilities, initialStoredValue]);

    const handleChange = (event) => {
      const newValue = event.target.value;
      // Only trigger change if it's valid
      if (allowedFacilities.some((facility) => facility.id === newValue)) {
        setSelectedFacility(newValue);
        localStorage.setItem('selectedFacility', newValue);
        onChange(newValue);
      }
    };

    useImperativeHandle(ref, () => ({
      changeFacility: handleChange,
    }));

    return (
      <div className="FacilitySelectionContainer">
        <Select
          value={selectedFacility}
          onChange={handleChange}
          displayEmpty
          inputProps={{ 'aria-label': 'Facility selector' }}
          sx={{ border: 'none' }}
        >
          {(allowedFacilities.length > 0 ? allowedFacilities : facilities).map(
            (facility) => (
              <MenuItem
                key={facility.id}
                value={facility.id}
                disabled={
                  !allowedFacilities.map((f) => f.id).includes(facility.id)
                }
              >
                {facility.name}
              </MenuItem>
            ),
          )}
        </Select>
      </div>
    );
  },
);

DashboardNavbarFacilitySelection.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialValue: PropTypes.string,
  allowedFacilities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      logo: PropTypes.string,
    }),
  ).isRequired,
};

export default DashboardNavbarFacilitySelection;
